import { AxiosError } from 'axios'
import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'

import router from '@/router'
import { setRouteEmitter } from '@/utils/route-listener.ts'

type AuthStore = ReturnType<typeof useAuth>

// Constants for better maintainability
const ROUTES = {
  ALLOW_LIST: ['/login', '/error', '/401', '/404', '/403'] as const,
  LOGIN_PATH: '/login',
  HOME_PATH: '/',
  REDIRECT_PREFIX: '/redirect',
} as const

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
} as const

// Helper functions để tách logic
const isPublicRoute = (path: string): boolean => {
  return ROUTES.ALLOW_LIST.includes(path as any) || path.startsWith(ROUTES.REDIRECT_PREFIX)
}

const isLoginRoute = (path: string): boolean => {
  return path === ROUTES.LOGIN_PATH
}

const handleUnauthenticatedUser = (to: RouteLocationNormalized): boolean => {
  if (!isPublicRoute(to.path)) {
    useLogoutRedirect()
    return true // Stop navigation
  }
  return false
}

const handleUserInfoFetch = async (
  auth: AuthStore,
  to: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<boolean> => {
  try {
    await auth.getUser()
    const currentRoute = await auth.generateDynamicRoutes()
    router.addRoute(currentRoute)
    next({
      ...to,
      replace: true,
    })
    return true // Stop navigation
  } catch (error) {
    const isUnauthorized =
      error instanceof AxiosError && error?.response?.status === HTTP_STATUS.UNAUTHORIZED

    if (isUnauthorized) {
      useLogoutRedirect()
      return true // Stop navigation
    }

    // Log other errors for debugging
    console.warn('Failed to fetch user info:', error)
    return false // Continue navigation for other errors
  }
}

const handleAuthenticatedUser = async (
  auth: AuthStore,
  to: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<boolean> => {
  // Redirect to home if trying to access login page
  if (isLoginRoute(to.path)) {
    next({ path: ROUTES.HOME_PATH })
    return true // Stop navigation
  }

  // Check if user info is needed
  if (!auth.user && auth.isSignedIn && !isPublicRoute(to.path)) {
    return await handleUserInfoFetch(auth, to, next)
  }

  return false // Continue navigation
}

const handleRolePermission = async (
  to: RouteLocationNormalized,
  auth: AuthStore
): Promise<boolean> => {
  if (to.meta?.uriRole && !auth.hasUri(to.meta.uriRole as string)) {
    await useErrorRedirect(HTTP_STATUS.FORBIDDEN, to.path)
    return true // Stop navigation
  }

  return false
}

// Main navigation guard with optimized flow
router.beforeEach(async (to, _, next) => {
  setRouteEmitter(to)
  const auth = useAuth()

  try {
    if (!auth.isSignedIn) {
      // call refresh token
      await auth.getAuthInfo()
    }
    // Handle unauthenticated users
    if (!auth.isSignedIn) {
      if (handleUnauthenticatedUser(to)) return
    } else {
      // Handle authenticated users
      if ((await handleAuthenticatedUser(auth, to, next)) || (await handleRolePermission(to, auth)))
        return
    }

    // Continue with navigation
    next()
  } catch (error) {
    console.error('Navigation guard error:', error)
    // Fallback to login on unexpected errors
    useLogoutRedirect()
  }
})

router.afterEach(to => {
  useMetaTitle(to)
  useLoadingCheck()
  useScrollToTop()
})
